import { useAuth } from '../../providers/auth';
import {
  WA_PERMISSION_PATH,
  WA_PATHS,
  WA_CHAT_ACTIONS,
  WA_KANBAN_ACTIONS,
  WA_CONTATOS_ACTIONS,
} from '../../services/whatsapp/waPermissions';

/**
 * Hook que retorna as permissoes do usuario no modulo WhatsApp.
 *
 * Permissoes granulares por sub-modulo:
 *   chat     — assumir (fila/depto/todos), transferir (proprio/depto/todos), responder, etc.
 *   kanban   — visualizar, criar, editar (proprio/todos), excluir, mover
 *   contatos — visualizar, criar, editar, excluir, iniciar conversa
 *
 * - Admin (grupo_id === 1) tem todas as permissoes automaticamente.
 * - Se as telas do WhatsApp ainda nao foram cadastradas no backend,
 *   libera tudo como default para nao travar o sistema durante o setup.
 */
export function useWaPermissions() {
  const { hasPermission, isAdmin, user } = useAuth();

  // Admin tem tudo
  if (isAdmin()) {
    return buildAllTrue();
  }

  // Verifica se existem permissoes de WhatsApp configuradas
  const currentUser = user as any;
  const telasPermissoes = currentUser?.telas_permissoes ?? [];
  const hasAnyWaPermission = telasPermissoes.some(
    (t: any) => t.path === WA_PERMISSION_PATH || t.path?.startsWith('/whatsapp'),
  );

  // Sem permissoes cadastradas: libera tudo (bootstrap)
  if (!hasAnyWaPermission) {
    return buildAllTrue();
  }

  // Permissoes granulares
  const canChat = (action: string): boolean => hasPermission(WA_PATHS.CHAT, action);
  const canKanban = (action: string): boolean => hasPermission(WA_PATHS.KANBAN, action);
  const canContato = (action: string): boolean => hasPermission(WA_PATHS.CONTATOS, action);

  return {
    // ─── Chat / Atendimento ───
    // Assumir
    canAssumirFila: canChat(WA_CHAT_ACTIONS.ASSUMIR_FILA),
    canAssumirDepartamento: canChat(WA_CHAT_ACTIONS.ASSUMIR_DEPARTAMENTO),
    canAssumirTodos: canChat(WA_CHAT_ACTIONS.ASSUMIR_TODOS),
    /** Atalho: pode assumir algo (fila OU depto OU todos) */
    canAssumir:
      canChat(WA_CHAT_ACTIONS.ASSUMIR_FILA) ||
      canChat(WA_CHAT_ACTIONS.ASSUMIR_DEPARTAMENTO) ||
      canChat(WA_CHAT_ACTIONS.ASSUMIR_TODOS),

    // Transferir
    canTransferirProprio: canChat(WA_CHAT_ACTIONS.TRANSFERIR_PROPRIO),
    canTransferirDepartamento: canChat(WA_CHAT_ACTIONS.TRANSFERIR_DEPARTAMENTO),
    canTransferirTodos: canChat(WA_CHAT_ACTIONS.TRANSFERIR_TODOS),
    /** Atalho: pode transferir algo (proprio OU depto OU todos) */
    canTransferir:
      canChat(WA_CHAT_ACTIONS.TRANSFERIR_PROPRIO) ||
      canChat(WA_CHAT_ACTIONS.TRANSFERIR_DEPARTAMENTO) ||
      canChat(WA_CHAT_ACTIONS.TRANSFERIR_TODOS),

    // Outras acoes de chat
    canResponder: canChat(WA_CHAT_ACTIONS.RESPONDER),
    canNotaInterna: canChat(WA_CHAT_ACTIONS.NOTA_INTERNA),
    canFinalizar: canChat(WA_CHAT_ACTIONS.FINALIZAR),
    canAbrirChamado: canChat(WA_CHAT_ACTIONS.ABRIR_CHAMADO),

    // ─── Kanban ───
    canKanbanVisualizar: canKanban(WA_KANBAN_ACTIONS.VISUALIZAR),
    canKanbanCriar: canKanban(WA_KANBAN_ACTIONS.CRIAR),
    canKanbanEditarProprio: canKanban(WA_KANBAN_ACTIONS.EDITAR_PROPRIO),
    canKanbanEditarTodos: canKanban(WA_KANBAN_ACTIONS.EDITAR_TODOS),
    canKanbanExcluir: canKanban(WA_KANBAN_ACTIONS.EXCLUIR),
    canKanbanMover: canKanban(WA_KANBAN_ACTIONS.MOVER),

    // ─── Contatos ───
    canContatoVisualizar: canContato(WA_CONTATOS_ACTIONS.VISUALIZAR),
    canContatoCriar: canContato(WA_CONTATOS_ACTIONS.CRIAR),
    canContatoEditar: canContato(WA_CONTATOS_ACTIONS.EDITAR),
    canContatoExcluir: canContato(WA_CONTATOS_ACTIONS.EXCLUIR),
    canContatoIniciarConversa: canContato(WA_CONTATOS_ACTIONS.INICIAR_CONVERSA),
  };
}

/** Retorna todas as permissoes como true (admin / bootstrap) */
function buildAllTrue() {
  return {
    canAssumirFila: true,
    canAssumirDepartamento: true,
    canAssumirTodos: true,
    canAssumir: true,

    canTransferirProprio: true,
    canTransferirDepartamento: true,
    canTransferirTodos: true,
    canTransferir: true,

    canResponder: true,
    canNotaInterna: true,
    canFinalizar: true,
    canAbrirChamado: true,

    canKanbanVisualizar: true,
    canKanbanCriar: true,
    canKanbanEditarProprio: true,
    canKanbanEditarTodos: true,
    canKanbanExcluir: true,
    canKanbanMover: true,

    canContatoVisualizar: true,
    canContatoCriar: true,
    canContatoEditar: true,
    canContatoExcluir: true,
    canContatoIniciarConversa: true,
  };
}
